import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import {
  BENEFIT_LABELS,
  SERVICE_LINES,
} from "pages/HospitalsPage/components/BenefitSelection/constants";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";
import {
  IPayerOverrideRow,
  IPayerOverrideSaveRequest,
  PayerOverrideKind,
} from "types/BenefitSelection/BenefitSelection";

import { KIND_CHOICES } from "./payerOverrideWording";

/** One plan name, whole. The name never wraps mid-word, so a comma inside it reads as part of it. */
const PlanNameChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  background: var(--surface-200, #e9ecef);
  font-size: 0.8125rem;

  button {
    display: inline-flex;
    align-items: center;
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    color: inherit;
  }
`;

/**
 * The service lines an override may stand behind — the API's `SERVICE_LINES`, which is the five care
 * settings plus MRI/CAT Scan and deliberately NOT plan level: the plan block carries no cost share,
 * so an override waiting on its silence would wait forever.
 */
const SERVICE_LINE_CHOICES: Array<{ label: string; value: string }> = [
  ...SERVICE_LINES.map((line) => ({
    label: line.label,
    value: line.value as string,
  })),
  { label: "MRI/CAT Scan", value: "MRI_CT" },
];

const BENEFIT_CHOICES = [
  "FLAT_COPAY",
  "COINSURANCE_PERCENT",
  "PROFESSIONAL_COPAY",
  "REMAINING_DEDUCTIBLE",
  "REMAINING_OOP",
].map((benefit) => ({ label: BENEFIT_LABELS[benefit], value: benefit }));

interface IDraft {
  kind: PayerOverrideKind;
  benefit: string;
  afterSource: string | null;
  correctsZero: boolean;
  value: number | null;
  accountId: string | null;
  practiceTypeCode: string;
  payerId: number | null;
  requestedPayerName: string;
  planNames: string[];
  /**
   * The plan name typed into the field and not yet turned into a chip.
   *
   * 🚨 IT LIVES IN THE DRAFT SO SAVE CAN FLUSH IT. A name typed and not confirmed with Enter is a
   * name the editor believes they entered; losing it on Save writes an override with a WIDER scope
   * than the one on screen — the one failure of this field that produces no error at all.
   */
  planNameInput: string;
  policyType: string;
  /**
   * CLE-2792 — SWAP only. The service line the corrected value must have been READ FROM; null means
   * whatever line it came from.
   */
  readFromSource: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  reason: string;
}

const toDraft = (row: IPayerOverrideRow | null): IDraft => ({
  kind: row?.kind ?? "ALWAYS",
  benefit: row?.benefit ?? "FLAT_COPAY",
  afterSource: row?.afterSource ?? null,
  correctsZero: row?.correctsZero ?? false,
  value: row?.value ?? null,
  accountId: row?.accountId ?? null,
  practiceTypeCode: row?.practiceTypeCode ?? "",
  payerId: row?.payerId ?? null,
  requestedPayerName: row?.requestedPayerName ?? "",
  planNames: row?.planNames ?? [],
  planNameInput: "",
  policyType: row?.policyType ?? "",
  readFromSource: row?.readFromSource ?? null,
  effectiveFrom: row?.effectiveFrom ?? null,
  effectiveTo: row?.effectiveTo ?? null,
  reason: row?.reason ?? "",
});

/** Trimmed to null for everything the API trims anyway. NOT for the requested payer name. */
const orNull = (value: string): string | null =>
  value.trim().length > 0 ? value.trim() : null;

/**
 * One more plan name on the list, trimmed, unless it is blank or already there.
 *
 * 🚨 THE WHOLE NAME IS ONE ENTRY. `BASIC SECONDARY,PRIMARY DISABILITY,BASIC` is a single plan
 * pVerify reports, so nothing here splits on a comma: three names that match no plan at all is an
 * override that saves, shows on screen, and governs nothing.
 *
 * Compared case-insensitively because the engine matches case-insensitively — two spellings are one
 * plan, and keeping both would print it twice and change no estimate. The first spelling is kept,
 * exactly as typed apart from the trim.
 */
export const addPlanName = (names: string[], raw: string): string[] => {
  const name = raw.trim();
  if (name.length === 0) return names;
  if (names.some((existing) => existing.toUpperCase() === name.toUpperCase())) {
    return names;
  }
  return [...names, name];
};

interface IPayerOverrideDialogProps {
  visible: boolean;
  /** The override being edited, or null when adding. */
  row: IPayerOverrideRow | null;
  saving: boolean;
  onSave: (body: IPayerOverrideSaveRequest) => void;
  onHide: () => void;
}

/**
 * CLE-2792 — one PAYER OVERRIDE.
 *
 * 🚨 THE THREE RADIOS ARE THE WHOLE DIALOG. They are the difference between a figure that ignores
 * the response, a figure that only fills a silence, and a figure that corrects a wrong reading —
 * 514 checks out of 688 apart on the one rule that has been measured. They are written as sentences
 * about the payer, and the words "replacing", "correcting", "mode" and "after source" appear
 * nowhere: an editor picking between synonyms out of the engine's vocabulary picks wrongly.
 *
 * Every empty scope field means "every", and leaving them all empty IS how an editor says an
 * override applies to every payer and every account (Mike, 2026-09-14). An earlier version added a
 * confirmation checkbox for that case; it said nothing the empty fields did not, and is gone.
 *
 * 🚨 PLAN NAMES ARE A LIST AND A NAME MAY CONTAIN A COMMA (Ani, 2026-09-14). One override may name
 * several plans — the five PPO BASIC rows were one rule written five times — and
 * `BASIC SECONDARY,PRIMARY DISABILITY,BASIC` is ONE of those names. So a name is committed on ENTER
 * and on blur, never on a separator key, and Save flushes whatever is still typed: a dropped name is
 * an override saved with a wider scope than the one on screen, and nothing reports it.
 *
 * ⚠️ THE REQUESTED PAYER NAME IS NOT THE PAYER DROPDOWN. It is the string the estimator picked,
 * compared exactly, because that is what the rule it replaces compared. A payer renamed in our
 * database stops matching in legacy, so keying on the id instead would MOVE A NUMBER.
 */
export function PayerOverrideDialog({
  visible,
  row,
  saving,
  onSave,
  onHide,
}: IPayerOverrideDialogProps) {
  const [draft, setDraft] = useState<IDraft>(toDraft(row));

  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });
  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });

  const payerOptions = sortBy(
    ((payersData as any)?.data || payersData || []).map((payer: any) => ({
      label: payer.name,
      value: payer.id,
    })),
    ["label"]
  );

  const accountOptions = sortBy(
    ((accountsData as any)?.data || accountsData || []).map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );

  useEffect(() => {
    if (!visible) return;
    setDraft(toDraft(row));
    // `row` and `visible` only: a dependency on the option lists would reset the form under the
    // editor's hands the moment either query settles.
  }, [visible, row]);

  const set = (patch: Partial<IDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const commitPlanName = () =>
    setDraft((current) => ({
      ...current,
      planNames: addPlanName(current.planNames, current.planNameInput),
      planNameInput: "",
    }));

  const removePlanName = (name: string) =>
    setDraft((current) => ({
      ...current,
      planNames: current.planNames.filter((existing) => existing !== name),
    }));

  const canSave =
    draft.value !== null &&
    draft.value >= 0 &&
    draft.reason.trim().length > 0 &&
    (draft.kind !== "WHEN_MISSING" || draft.afterSource !== null);

  const handleSave = () => {
    onSave({
      kind: draft.kind,
      benefit: draft.benefit,
      // Sent only on the shape that has one: the API refuses an `afterSource` on the other two
      // rather than ignoring it, so a stale value left behind by switching kinds would 400.
      afterSource: draft.kind === "WHEN_MISSING" ? draft.afterSource : null,
      correctsZero: draft.kind === "SWAP" ? draft.correctsZero : false,
      // Same rule as `afterSource` above and for the same reason: the API refuses this on the other
      // two shapes rather than ignoring it, so a value left behind by switching kinds would 400.
      readFromSource: draft.kind === "SWAP" ? draft.readFromSource : null,
      value: draft.value as number,
      accountId: draft.accountId,
      practiceTypeCode: orNull(draft.practiceTypeCode),
      payerId: draft.payerId,
      // 🚨 SENT UNTRIMMED. The engine compares it with `===` against what the estimator picked, so
      // tidying it here would stop it matching the request it was copied from — silently.
      requestedPayerName:
        draft.requestedPayerName.trim().length > 0
          ? draft.requestedPayerName
          : null,
      // 🚨 THE PENDING TEXT IS FLUSHED HERE. Saving with a name typed and not confirmed must not
      // drop it — see `IDraft.planNameInput`.
      planNames: addPlanName(draft.planNames, draft.planNameInput),
      policyType: orNull(draft.policyType),
      effectiveFrom: draft.effectiveFrom,
      effectiveTo: draft.effectiveTo,
      reason: draft.reason.trim(),
    });
  };

  const isPercent = draft.benefit === "COINSURANCE_PERCENT";

  return (
    <Dialog
      header={row ? "Edit payer override" : "Add a payer override"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(50rem, 95vw)" }}
      data-testid="payerOverrideDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            data-testid="payerOverrideDialog.cancel"
            onClick={onHide}
          />
          <Button
            className="p-button-sm"
            type="button"
            label={row ? "Save override" : "Add override"}
            loading={saving}
            disabled={!canSave}
            data-testid="payerOverrideDialog.save"
            onClick={handleSave}
          />
        </div>
      }
    >
      <Surface className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <Label htmlFor="payerOverrideDialog.kind">What is wrong</Label>
          {KIND_CHOICES.map((choice) => (
            <div key={choice.value} className="flex flex-column gap-1">
              <span className="flex align-items-center gap-2">
                <RadioButton
                  inputId={`payerOverrideKind.${choice.value}`}
                  data-testid={`payerOverrideDialog.kind.${choice.value}`}
                  checked={draft.kind === choice.value}
                  onChange={() =>
                    set({
                      kind: choice.value,
                      // Switching away from the one shape that carries them clears both, so a
                      // request never names a field its own kind forbids.
                      afterSource:
                        choice.value === "WHEN_MISSING"
                          ? draft.afterSource
                          : null,
                      correctsZero:
                        choice.value === "SWAP" ? draft.correctsZero : false,
                      readFromSource:
                        choice.value === "SWAP" ? draft.readFromSource : null,
                    })
                  }
                />
                <label
                  htmlFor={`payerOverrideKind.${choice.value}`}
                  className="text-sm"
                >
                  {choice.label}
                </label>
              </span>
              <small className="text-color-secondary ml-5">{choice.help}</small>
            </div>
          ))}
        </div>

        {draft.kind === "WHEN_MISSING" && (
          <div className="field">
            <Label htmlFor="payerOverrideDialog.afterSource">
              Service line
            </Label>
            <Dropdown
              id="payerOverrideDialog.afterSource"
              data-testid="payerOverrideDialog.afterSource"
              className="w-full"
              options={SERVICE_LINE_CHOICES}
              placeholder="Choose a service line"
              value={draft.afterSource}
              onChange={(e) => set({ afterSource: e.value })}
            />
          </div>
        )}

        {draft.kind === "SWAP" && (
          <div className="field">
            <Label htmlFor="payerOverrideDialog.readFromSource">
              Only when the value was read from
            </Label>
            <Dropdown
              id="payerOverrideDialog.readFromSource"
              data-testid="payerOverrideDialog.readFromSource"
              className="w-full"
              options={SERVICE_LINE_CHOICES}
              showClear
              placeholder="Any service line"
              value={draft.readFromSource}
              onChange={(e) => set({ readFromSource: e.value ?? null })}
            />
            <small className="text-color-secondary">
              Leave this empty to swap the amount wherever it was read. Name a
              service line where the payer reports the wrong amount on that line
              only — HNE sends the hospital rate on the ambulatory surgery
              center line, and a hospital outpatient department really does owe
              the rate it sends.
            </small>
          </div>
        )}

        {draft.kind === "SWAP" && (
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="payerOverrideDialog.correctsZero"
              data-testid="payerOverrideDialog.correctsZero"
              checked={draft.correctsZero}
              onChange={(e) => set({ correctsZero: Boolean(e.checked) })}
            />
            <label
              htmlFor="payerOverrideDialog.correctsZero"
              className="text-sm"
            >
              Also swap a reported $0
            </label>
          </div>
        )}

        <div className="formgrid grid">
          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.benefit">Benefit</Label>
            <Dropdown
              id="payerOverrideDialog.benefit"
              data-testid="payerOverrideDialog.benefit"
              className="w-full"
              options={BENEFIT_CHOICES}
              value={draft.benefit}
              onChange={(e) => set({ benefit: e.value })}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.value">
              {isPercent ? "Percent" : "Amount"}
            </Label>
            <InputNumber
              id="payerOverrideDialog.value"
              data-testid="payerOverrideDialog.value"
              className="w-full"
              inputId="payerOverrideDialog.valueInput"
              mode={isPercent ? "decimal" : "currency"}
              currency="USD"
              locale="en-US"
              suffix={isPercent ? "%" : undefined}
              min={0}
              max={isPercent ? 100 : undefined}
              value={draft.value}
              onValueChange={(e) => set({ value: e.value ?? null })}
            />
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <span className="font-medium">Scope</span>
          <small className="text-color-secondary">
            Anything left empty means every one. An override applies only where
            every field below matches.
          </small>
        </div>

        <div className="formgrid grid">
          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.payerId">Payer</Label>
            <Dropdown
              id="payerOverrideDialog.payerId"
              data-testid="payerOverrideDialog.payerId"
              className="w-full"
              options={payerOptions}
              filter
              showClear
              placeholder="Every payer"
              value={draft.payerId}
              onChange={(e) => set({ payerId: e.value ?? null })}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.requestedPayerName">
              Requested payer name
            </Label>
            <InputText
              id="payerOverrideDialog.requestedPayerName"
              data-testid="payerOverrideDialog.requestedPayerName"
              className="w-full"
              value={draft.requestedPayerName}
              onChange={(e) => set({ requestedPayerName: e.target.value })}
            />
            <small className="text-color-secondary">
              Exactly as the estimator types it.
            </small>
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.planNames.input">
              Plan names
            </Label>
            {draft.planNames.length > 0 && (
              <div
                className="flex flex-wrap gap-1 mb-2"
                data-testid="payerOverrideDialog.planNames"
              >
                {draft.planNames.map((name) => (
                  <PlanNameChip key={name}>
                    <span data-testid={`payerOverrideDialog.planNames.${name}`}>
                      {name}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      data-testid={`payerOverrideDialog.planNames.remove.${name}`}
                      onClick={() => removePlanName(name)}
                    >
                      <i className="pi pi-times" />
                    </button>
                  </PlanNameChip>
                ))}
              </div>
            )}
            <InputText
              id="payerOverrideDialog.planNames.input"
              data-testid="payerOverrideDialog.planNames.input"
              className="w-full"
              value={draft.planNameInput}
              onChange={(e) => set({ planNameInput: e.target.value })}
              // 🚨 ENTER, AND NOTHING ELSE, ENDS A NAME. A plan name may contain a comma —
              // `BASIC SECONDARY,PRIMARY DISABILITY,BASIC` is one plan — so a separator key would
              // cut a real name into pieces that match nothing.
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                commitPlanName();
              }}
              // Blur commits too, so clicking straight from the field to Save keeps the name; Save
              // flushes whatever is still pending for the paths blur does not cover.
              onBlur={() => commitPlanName()}
            />
            <small className="text-color-secondary">
              Press Enter after each plan name — names may contain commas. Each
              must be a whole plan name, not part of one. Leave it empty for
              every plan.
            </small>
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.policyType">Policy type</Label>
            <InputText
              id="payerOverrideDialog.policyType"
              data-testid="payerOverrideDialog.policyType"
              className="w-full"
              value={draft.policyType}
              onChange={(e) => set({ policyType: e.target.value })}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.accountId">Account</Label>
            <Dropdown
              id="payerOverrideDialog.accountId"
              data-testid="payerOverrideDialog.accountId"
              className="w-full"
              options={accountOptions}
              filter
              showClear
              placeholder="Every account"
              value={draft.accountId}
              onChange={(e) => set({ accountId: e.value ?? null })}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.practiceTypeCode">
              Service type code
            </Label>
            <InputText
              id="payerOverrideDialog.practiceTypeCode"
              data-testid="payerOverrideDialog.practiceTypeCode"
              className="w-full"
              value={draft.practiceTypeCode}
              onChange={(e) => set({ practiceTypeCode: e.target.value })}
            />
            <small className="text-color-secondary">
              The code the check was sent with, e.g. 73.
            </small>
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.effectiveFrom">
              Effective from (optional)
            </Label>
            <Calendar
              id="payerOverrideDialog.effectiveFrom"
              data-testid="payerOverrideDialog.effectiveFrom"
              className="w-full"
              value={draft.effectiveFrom ? new Date(draft.effectiveFrom) : null}
              onChange={(e) =>
                set({
                  effectiveFrom: e.value
                    ? (e.value as Date).toISOString()
                    : null,
                })
              }
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="payerOverrideDialog.effectiveTo">
              Effective to (optional)
            </Label>
            <Calendar
              id="payerOverrideDialog.effectiveTo"
              data-testid="payerOverrideDialog.effectiveTo"
              className="w-full"
              value={draft.effectiveTo ? new Date(draft.effectiveTo) : null}
              onChange={(e) =>
                set({
                  effectiveTo: e.value ? (e.value as Date).toISOString() : null,
                })
              }
            />
          </div>

          <div className="field col-12">
            <Label htmlFor="payerOverrideDialog.reason">
              Why — one sentence, shown to staff on the estimate this override
              changes
            </Label>
            <InputTextarea
              id="payerOverrideDialog.reason"
              data-testid="payerOverrideDialog.reason"
              className="w-full"
              rows={2}
              value={draft.reason}
              onChange={(e) => set({ reason: e.target.value })}
            />
          </div>
        </div>

        {!canSave && (
          <span
            className="text-sm text-color-secondary"
            data-testid="payerOverrideDialog.incomplete"
          >
            An override needs an amount, a one-sentence reason, and a service
            line where one is asked for. Scope fields left empty mean every
            payer, plan or account.
          </span>
        )}
      </Surface>
    </Dialog>
  );
}
